import { formatUserFacingError } from '@/lib/errors'
import { useState, useCallback, useMemo } from 'react'
import {
  adminUserLinksService,
  type LinkUserRow,
  type LinkIntegrityIssue,
} from '../services/admin.userLinks.service'
import { notificationsService } from '@/features/notifications'
import type { CompanyMemberDocument } from '@/types'

export function useAdminUserLinks() {
  const [users, setUsers] = useState<LinkUserRow[]>([])
  const [members, setMembers] = useState<Array<CompanyMemberDocument & { id: string }>>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    const [usersResult, membersResult] = await Promise.allSettled([
      adminUserLinksService.getAllUsers(),
      adminUserLinksService.getAllMembers(),
    ])
    if (usersResult.status === 'fulfilled') {
      setUsers(usersResult.value)
    } else {
      const msg = formatUserFacingError(usersResult.reason, 'Failed to load users')
      setError(`Users: ${msg}`)
    }
    if (membersResult.status === 'fulfilled') {
      setMembers(membersResult.value)
    } else {
      const msg = formatUserFacingError(membersResult.reason, 'Failed to load members')
      setError((prev) => prev ? `${prev} | Members: ${msg}` : `Members: ${msg}`)
    }
    setLoading(false)
  }, [])

  const link = useCallback(async (userId: string, memberId: string) => {
    await adminUserLinksService.linkUserToMember(userId, memberId)
    await refresh()
  }, [refresh])

  const unlink = useCallback(async (userId: string, memberId: string) => {
    await adminUserLinksService.unlinkUserFromMember(userId, memberId)
    await refresh()
  }, [refresh])

  const backfillProfiles = useCallback(async () => {
    const result = await adminUserLinksService.backfillProfiles()
    await refresh()
    return result
  }, [refresh])

  const repairLink = useCallback(async (userId: string, memberId: string) => {
    await adminUserLinksService.repairLink(userId, memberId)
    await refresh()
  }, [refresh])

  const resolveNotification = useCallback(
    async (notificationId: string, resolvedByUid: string) => {
      await notificationsService.resolve(notificationId, resolvedByUid)
    },
    []
  )

  const integrityIssues: LinkIntegrityIssue[] = useMemo(
    () => adminUserLinksService.computeIntegrityIssues(users, members),
    [users, members]
  )

  return {
    users,
    members,
    loading,
    error,
    integrityIssues,
    link,
    unlink,
    backfillProfiles,
    repairLink,
    resolveNotification,
    refresh,
  }
}
